<template>
  <div>
    <p class="lead d-flex justify-content-between">
      <span>{{ headerLabel }}</span>
      <span class="small">{{ action.data.timestamp }}</span>
    </p>
    <div
      v-if="showsResponseFormFollowUpBlock"
      class="mb-2 pl-1 border-bottom pb-2"
    >
      <div
        v-if="action.data.responseFormLogStatus === 'ready'"
        class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center flex-wrap"
      >
        <div
          v-if="action.data.responseFormDragPayload"
          class="d-flex align-items-center mb-1"
          draggable="true"
          @dragstart="onResponseFormDragStart"
          @dragend="onResponseFormDragEnd"
        >
          <span
            class="entity-link mr-1 cursor-pointer"
            @click="openResponseFormInNewTab"
          >
            {{ responseFormLinkLabel }}
          </span>
          <feather-icon
            icon="ExternalLinkIcon"
            size="16"
            class="cursor-pointer text-primary"
            @click="openResponseFormInNewTab"
          />
        </div>
        <small
          v-if="action.data.responseFormDragPayload"
          class="text-muted ml-sm-1 mb-1"
        >
          {{ $t("action_plan.viewer.drag_form_hint") }}
        </small>
      </div>
      <div
        v-if="action.data.responseFormLogStatus === 'ready' && !action.data.form_answered && !action.data.continue && !terminated"
        class="mt-1"
      >
        <b-button
          variant="primary"
          size="sm"
          :disabled="isVerifyingFormAnswer"
          @click="onVerifyFormAnswered"
        >
          <feather-icon icon="CheckCircleIcon" size="18" class="mr-25"/>
          {{ $t("action_plan.viewer.advance") }}
        </b-button>
      </div>
      <div
        v-else-if="action.data.responseFormLogStatus === 'missing_id'"
        class="text-warning small"
      >
        {{ $t("action_plan.viewer.response_form_id_missing") }}
      </div>
    </div>
    <div v-for="(cmd, idx) in action.node_data.filled_stack" :key="idx">
      <!-- Command -->
      <template v-if="cmd.type === 'cmd'">
        <div class="cmd-card">
          <div class="cmd-card__header">
            <span class="cmd-card__icon">
              <feather-icon icon="PlayCircleIcon" size="16"/>
            </span>
            <span class="cmd-card__name">{{ resolveNodeName(cmd) }}</span>

            <!-- Panel failure: yes/no instead of a bare execute button -->
            <template v-if="isPanelFailureNode">
              <div
                class="d-flex align-items-center"
                style="gap: 0.5rem"
                v-if="!panelFailureAnswer && !terminated"
              >
                <b-button
                  variant="primary"
                  size="sm"
                  @click="onPanelFailureAnswer('yes')"
                >
                  {{ $t("action_plan.viewer.panel_failure_yes") }}
                </b-button>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="onPanelFailureAnswer('no')"
                >
                  {{ $t("action_plan.viewer.panel_failure_no") }}
                </b-button>
              </div>
              <span
                class="cmd-card__status cmd-card__status--muted"
                v-else-if="panelFailureAnswer === 'no'"
              >
                <feather-icon icon="MinusCircleIcon" size="14" class="mr-25"/>
                {{ $t("action_plan.viewer.panel_failure_answered_no") }}
              </span>
            </template>

            <b-button
              v-else-if="!action.data.executed"
              variant="primary"
              size="sm"
              @click="onExecuteCmdClick(action)"
              :disabled="terminated"
            >
              <feather-icon icon="PlayCircleIcon" size="16" class="mr-25"/>
              {{ $t("action_plan.viewer.execute") }}
            </b-button>

            <!-- Command status ok -->
            <span
              class="cmd-card__status cmd-card__status--success"
              v-if="action.data.executed && action.data.status === 'OK'"
            >
              <feather-icon icon="CheckCircleIcon" size="14" class="mr-25"/>
              {{ $t("action_plan.viewer.executed_success") }}
            </span>

            <!-- Command status error -->
            <span
              class="cmd-card__status cmd-card__status--error"
              v-if="action.data.executed && action.data.status === 'ERROR'"
            >
              <feather-icon icon="XCircleIcon" size="14" class="mr-25"/>
              {{ $t("action_plan.viewer.executed_error") }}
            </span>
          </div>

          <div
            v-if="!isResponseOperationalFormCmd(cmd) && parseCmdDataForDisplay(cmd)"
            class="cmd-card__body small"
            v-html="parseCmdDataForDisplay(cmd)"
          ></div>

          <div
            v-if="isPanelFailureNode && panelFailureAnswer === 'yes'"
            class="cmd-card__body"
          >
            <label
              :for="`panel-failure-comment-${action.id}`"
              v-b-tooltip.hover="$t('action_plan.viewer.panel_failure_comment_hint')"
            >
              {{ $t("action_plan.viewer.panel_failure_comment") }}
              <feather-icon icon="InfoIcon" size="14" class="ml-25 text-muted"/>
            </label>
            <b-form-textarea
              :id="`panel-failure-comment-${action.id}`"
              rows="3"
              max-rows="6"
              v-model="action.data.panelFailureComment"
              :placeholder="$t('action_plan.viewer.panel_failure_comment')"
              :disabled="terminated || validating || action.data.executed"
            ></b-form-textarea>
            <small class="text-muted d-block mt-25">
              {{ $t("action_plan.viewer.panel_failure_comment_hint") }}
            </small>
            <b-button
              class="mt-1"
              variant="primary"
              size="sm"
              :disabled="terminated || validating || isNotifyingPanelFailure"
              @click="onNotifyPanelFailureClick(action)"
              v-if="!action.data.executed"
            >
              <feather-icon icon="BellIcon" size="16" class="mr-25"/>
              {{ $t("action_plan.viewer.notify") }}
            </b-button>
          </div>
        </div>
        <div class="my-1">
          <label :for="`input-${action.id}`">
            {{ $t("action_plan.viewer.comments") }}
          </label>
          <input
            type="text"
            :id="`input-${action.id}`"
            v-model="action.data.comments"
            class="form-control"
            :placeholder="$t('action_plan.viewer.comments')"
            :disabled="terminated || validating"
          />
        </div>
        <div class="my-1">
          <files
            v-model="action.data.files"
            :action-plan-log-uuid="actionPlanLogUuid"
            :validating="validating"
            :preview="preview"
            :terminated="terminated"
            :action="action"
            @file-added="$emit('checkpoint')"
          />
        </div>
      </template>

      <!-- Delay -->
      <template v-else>
        <div class="cmd-card">
          <div class="cmd-card__header">
            <span class="cmd-card__icon">
              <feather-icon icon="PauseCircleIcon" size="16"/>
            </span>
            <span class="cmd-card__name">{{ resolveNodeName(cmd) }}</span>
            <span class="cmd-card__meta">
              {{ cmd.data }} {{ $t("commands.units.seconds") }}
            </span>
          </div>
        </div>
      </template>
    </div>
    <div class="text-right mb-1">
      <template v-if="!action.node_data.required">
        <b-button
          variant="primary"
          size="sm"
          @click="onContinueClick(action)"
          :disabled="validating || (showsResponseFormFollowUp && !action.data.form_answered)"
          v-if="!action.data.continue && !terminated"
        >
          {{ $t("action_plan.viewer.continue") }}
          <feather-icon icon="ChevronRightIcon" size="18"/>
        </b-button>
      </template>
      <template v-else>
        <b-button
          variant="primary"
          size="sm"
          @click="onContinueClick(action)"
          :disabled="!isNodeResolved || validating || (showsResponseFormFollowUp && !action.data.form_answered)"
          v-if="!action.data.continue && !terminated"
        >
          {{ $t("action_plan.viewer.continue") }}
          <feather-icon icon="ChevronRightIcon" size="18"/>
        </b-button>
      </template>
    </div>
  </div>
</template>

<script>
import commandStackHelperMixin from "@/mixins/commandStackHelperMixin";
import layoutMixin from "@/mixins/layoutMixin";
import axios from "@axios";
import { getUserData } from "@/auth/utils";
import Files from "./Files.vue";
import {
  isAssignOperationalFormTaskNodeData,
  isAssignOperationalFormTaskStackEntry,
  isResponseOperationalFormCmd,
  readAssignOperationalFormComment,
  toMergeableCmdData,
} from "@/libs/assignOperationalFormTask";
import { extractResponseFormAnswerFromRunCommand } from "@/libs/actionPlanRunCommandResponse";
import {
  mergeResponseOperationalFormAlarmFromContext,
  payloadContainsResponseOperationalForm,
} from "@/libs/responseOperationalForm";
import {
  mergeNotifyPanelFailureComment,
  payloadContainsNotifyPanelFailure,
  resolveNotifyPanelFailureDefaultComment,
} from "@/libs/panelFailure";
import { SLOT_TYPES } from "@/config/layoutConfig";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {Files},
  mixins: [commandStackHelperMixin, layoutMixin],
  data() {
    return {
      isVerifyingFormAnswer: false,
      isNotifyingPanelFailure: false,
    };
  },
  mounted() {
    this.restoreResponseFormDragPayloadIfNeeded();
  },
  props: {
    actionPlanLogUuid: String,
    alarmId: String | Number,
    action: Object,
    terminated: Boolean,
    preview: Boolean,
    validating: Boolean,
  },
  computed: {
    isPanelFailureNode() {
      return payloadContainsNotifyPanelFailure(this.action?.node_data);
    },
    panelFailureAnswer() {
      return this.action?.data?.panelFailureAnswer || null;
    },
    headerLabel() {
      if (this.isPanelFailureNode) {
        return this.$t("action_plan.viewer.panel_failure_title");
      }
      return this.$t("action_plan.viewer.execute_command");
    },
    isNodeResolved() {
      if (this.isPanelFailureNode && this.panelFailureAnswer === "no") {
        return true;
      }
      return !!this.action?.data?.executed;
    },
    showsResponseFormFollowUp() {
      return payloadContainsResponseOperationalForm(this.action?.node_data);
    },
    showsResponseFormFollowUpBlock() {
      const status = this.action?.data?.responseFormLogStatus;
      return (
        this.showsResponseFormFollowUp &&
        this.action?.data?.executed &&
        this.action?.data?.status === "OK" &&
        ["ready", "missing_id"].includes(status)
      );
    },
    responseFormLinkLabel() {
      const stackTemplateName = this.resolveFormTemplateNameFromStack();
      if (stackTemplateName) {
        return stackTemplateName;
      }
      const payload = this.action?.data?.responseFormDragPayload;
      const templateName =
        payload?.template?.name ||
        payload?.form_template?.name ||
        payload?.operational_security_form_template?.name;
      if (templateName) {
        return templateName;
      }
      const reference = this.action?.data?.responseFormReference;
      if (reference) {
        return reference;
      }
      const id = this.action?.data?.responseFormAnswerId;
      if (id != null) {
        return `#${id}`;
      }
      return this.$t("action_plan.viewer.start_form");
    },
  },
  methods: {
    isAssignOperationalFormTaskNodeData,
    isResponseOperationalFormCmd,
    resolveAssignFormComment(cmd) {
      const nodeData = this.action?.node_data;
      const stack = Array.isArray(nodeData?.filled_stack)
        ? nodeData.filled_stack
        : [];
      const sources = [
        cmd?.data,
        nodeData?.data,
        nodeData?.storage,
        nodeData,
        ...stack.map((entry) => entry?.data),
      ];
      for (const source of sources) {
        const comment = readAssignOperationalFormComment(source);
        if (comment) {
          return comment;
        }
      }
      return "";
    },
    parseCmdDataForDisplay(cmd) {
      if (cmd.type !== "cmd") {
        return "";
      }
      if (isAssignOperationalFormTaskStackEntry(cmd)) {
        return this.formatAssignOperationalFormTaskCmdData(
          this.resolveAssignFormComment(cmd)
        );
      }
      if (cmd.cmd === "ai_decision") {
        return this.formatAIDecisionCmdData(cmd.data);
      }
      return "";
    },
    restoreResponseFormDragPayloadIfNeeded() {
      const nodeData = this.action?.node_data;
      const data = this.action?.data;
      if (!nodeData || !data) {
        return;
      }
      if (!payloadContainsResponseOperationalForm(nodeData)) {
        return;
      }
      if (data.responseFormLogStatus !== "ready") {
        return;
      }
      if (data.responseFormAnswerId == null) {
        return;
      }
      if (data.responseFormDragPayload != null) {
        return;
      }
      void this.hydrateResponseFormDragPayload(
        this.action,
        data.responseFormAnswerId
      );
    },
    resolveFormTemplateNameFromStack() {
      const stack = this.action?.node_data?.filled_stack;
      if (!Array.isArray(stack)) {
        return null;
      }
      const formEntry = stack.find(
        (entry) => entry?.type === "cmd" && isResponseOperationalFormCmd(entry)
      );
      const templates = formEntry?.data?.operational_form_template;
      if (!Array.isArray(templates) || !templates.length) {
        return null;
      }
      return templates[0]?.name || null;
    },
    async onVerifyFormAnswered() {
      const formAnswerId = this.action?.data?.responseFormAnswerId;
      if (formAnswerId == null) {
        return;
      }
      this.isVerifyingFormAnswer = true;
      try {
        const { data: responseBody } = await axios.post(
          "/v1/operational-security-forms/security-form-answered",
          { id_form_answer: formAnswerId }
        );
        const isAnswered = responseBody?.data === true || responseBody === true;
        if (isAnswered) {
          this.$set(this.action.data, "form_answered", true);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("action_plan.viewer.form_answered_success"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("action_plan.viewer.form_not_answered"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      } catch {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("action_plan.viewer.form_not_answered"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.isVerifyingFormAnswer = false;
      }
    },
    onContinueClick(action) {
      this.$emit("continue", action);
    },
    onPanelFailureAnswer(answer) {
      this.$set(this.action.data, "panelFailureAnswer", answer);

      if (answer === "no") {
        this.$set(this.action.data, "panelFailureNotified", false);
        this.$emit("checkpoint");
        return;
      }

      if (this.action.data.panelFailureComment == null) {
        this.$set(
          this.action.data,
          "panelFailureComment",
          resolveNotifyPanelFailureDefaultComment(this.action.node_data)
        );
      }
      this.$emit("checkpoint");
    },
    async onNotifyPanelFailureClick(action) {
      if (this.isNotifyingPanelFailure) {
        return;
      }
      this.isNotifyingPanelFailure = true;
      try {
        await this.onExecuteCmdClick(action);
        if (action.data.status === "OK") {
          this.$set(action.data, "panelFailureNotified", true);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t("action_plan.viewer.panel_failure_notified"),
              icon: "BellIcon",
              variant: "success",
            },
          });
        }
        this.$emit("checkpoint");
      } finally {
        this.isNotifyingPanelFailure = false;
      }
    },
    buildRunCommandNodeData(nodeData) {
      const payload = JSON.parse(JSON.stringify(nodeData));
      if (this.isAssignOperationalFormTaskNodeData(nodeData)) {
        const executorId = getUserData()?.id;
        const commentText = this.resolveAssignFormComment(null);

        const mergeAssignOperationalFormFields = (value) => {
          const target = toMergeableCmdData(value);
          if (commentText) {
            target.comment = commentText;
          } else {
            delete target.comment;
          }
          if (executorId != null) {
            target.users = executorId;
          }
          return target;
        };

        if (payload.storage) {
          payload.storage = mergeAssignOperationalFormFields(payload.storage);
        }

        payload.data = mergeAssignOperationalFormFields(payload.data);

        const stack = payload.filled_stack;
        if (Array.isArray(stack)) {
          stack.forEach((entry) => {
            if (isAssignOperationalFormTaskStackEntry(entry)) {
              entry.data = mergeAssignOperationalFormFields(entry.data);
            }
          });
        }
      }
      if (this.isPanelFailureNode) {
        mergeNotifyPanelFailureComment(
          payload,
          this.action?.data?.panelFailureComment
        );
      }
      mergeResponseOperationalFormAlarmFromContext(payload, this.alarmId);
      return payload;
    },
    async onExecuteCmdClick(action) {
      if (!this.preview) {
        const cmdPayload = this.buildRunCommandNodeData(action.node_data);

        const data = {
          action_plan_log_id: this.actionPlanLogUuid,
          cmd: cmdPayload,
          id_alarm: this.alarmId,
        };
        try {
          const { data: responseBody } = await axios.post(
            "/v1/action_plan/run-command",
            data
          );
          this.$set(action.data, "executed", true);
          this.$set(action.data, "status", "OK");
          if (payloadContainsResponseOperationalForm(cmdPayload)) {
            const instant =
              extractResponseFormAnswerFromRunCommand(responseBody);
            if (instant) {
              this.applyResponseFormAnswerFields(action, instant);
            } else {
              this.$set(action.data, "responseFormLogStatus", "missing_id");
              this.$set(action.data, "responseFormDragPayload", null);
            }
          }
        } catch (error) {
          this.$set(action.data, "executed", true);
          this.$set(action.data, "status", "ERROR");
        }
      } else {
        this.$set(action.data, "executed", true);
        this.$set(action.data, "status", "OK");
      }
    },
    resolveNodeName(nodeData) {
      if (typeof nodeData.title === "string" && nodeData.title.trim() !== "") {
        return nodeData.title;
      }

      return nodeData.cmd !== "custom"
        ? this.$t(`commands.list.${nodeData.cmd_name}.name`)
        : nodeData.cmd_name;
    },
    applyResponseFormAnswerFields(action, fields) {
      this.$set(action.data, "responseFormDragPayload", null);
      this.$set(action.data, "responseFormLogStatus", "ready");
      this.$set(action.data, "responseFormAnswerId", fields.id_form_answer);
      this.$set(
        action.data,
        "responseFormReference",
        fields.reference != null ? fields.reference : ""
      );
      if (fields.id_template != null) {
        this.$set(action.data, "responseFormTemplateId", fields.id_template);
      }
      void this.hydrateResponseFormDragPayload(action, fields.id_form_answer);
    },
    async hydrateResponseFormDragPayload(action, idFormAnswer) {
      if (idFormAnswer == null) {
        this.$set(action.data, "responseFormDragPayload", null);
        return;
      }
      try {
        const { data: row } = await axios.get(
          `/v1/operational-security-forms/template-answers/${idFormAnswer}?withData=true`
        );
        const external = row.externalFields;
        const dataToReplace =
          external && typeof external === "object" ? { ...external } : {};
        this.$set(action.data, "responseFormDragPayload", {
          ...row,
          dataToReplace,
          currentSpecialForms: "",
        });
      } catch {
        this.$set(action.data, "responseFormDragPayload", null);
      }
    },
    openResponseFormInNewTab() {
      const id = this.action?.data?.responseFormAnswerId;
      if (id == null) {
        return;
      }
      const route = this.$router.resolve({
        path: "/operational_security/form-answers",
        query: { "open-id": String(id) },
      });
      window.open(route.href, "_blank");
    },
    onResponseFormDragStart(event) {
      const payload = this.action?.data?.responseFormDragPayload;
      if (!payload || payload.id == null) {
        return;
      }
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('text/plain', '');
      this.setSlotData(
        SLOT_TYPES.OPERATIONAL_SECURITY_FORM,
        "operational_security_form",
        {
          ...payload,
          dataToReplace: payload.dataToReplace || {},
          currentSpecialForms: payload.currentSpecialForms || "",
        },
        null,
        null
      );
    },
    onResponseFormDragEnd() {
      this.resetSlotSelection();
    },
  },
};
</script>

<style scoped>
.entity-link {
  cursor: pointer;
  color: #0052cc;
  text-decoration: none;
}

.entity-link:hover {
  text-decoration: underline;
}

.cursor-pointer {
  cursor: pointer;
}

.cmd-card {
  margin-bottom: 0.5rem;
  padding: 0.65rem 0.9rem;
  background-color: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
}

.cmd-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.cmd-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(27, 61, 94, 0.08);
  color: #1b3d5e;
}

.cmd-card__name {
  flex: 1 1 auto;
  min-width: 120px;
  font-weight: 600;
  color: #5e5873;
}

.cmd-card__meta {
  font-size: 0.85rem;
  color: #a5a2ad;
}

.cmd-card__status {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.3rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
}

.cmd-card__status--success {
  background-color: rgba(40, 199, 111, 0.12);
  color: #1f9d57;
}

.cmd-card__status--error {
  background-color: rgba(234, 84, 85, 0.12);
  color: #d43b3c;
}

.cmd-card__status--muted {
  background-color: rgba(110, 107, 123, 0.12);
  color: #6e6b7b;
}

.cmd-card__body {
  margin-top: 0.6rem;
  padding: 0.6rem 0 0 2.4rem;
  border-top: 1px solid #ebe9f1;
  color: #6e6b7b;
}

.dark-layout .cmd-card {
  background-color: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}

.dark-layout .cmd-card__icon {
  background-color: rgba(255, 255, 255, 0.08);
  color: #d0d2d6;
}

.dark-layout .cmd-card__name {
  color: #d0d2d6;
}

.dark-layout .cmd-card__meta {
  color: #8a91a5;
}

.dark-layout .cmd-card__status--success {
  background-color: rgba(40, 199, 111, 0.18);
  color: #63e6a5;
}

.dark-layout .cmd-card__status--error {
  background-color: rgba(234, 84, 85, 0.18);
  color: #f5a3a4;
}

.dark-layout .cmd-card__status--muted {
  background-color: rgba(255, 255, 255, 0.08);
  color: #b4b7bd;
}

.dark-layout .cmd-card__body {
  border-top-color: rgba(255, 255, 255, 0.08);
  color: #b4b7bd;
}
</style>
